import { LinkButton } from '@bordrio/ui-components';
import { useState } from 'react';
import { useGetPresignedDownloadUrlMutation } from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import { formatError } from '@src/services/error-formatter';
import Button, { ButtonColor } from '../Button';

interface Props {
  fileId: string;
  className?: string;
  color?: ButtonColor;
  children: React.ReactNode;
  asLink?: boolean;
}

function PresignedDownloadButton({
  fileId,
  className,
  color = 'primary',
  children,
  asLink,
}: Props): JSX.Element {
  const [createPresignedDownloadUrl] = useGetPresignedDownloadUrlMutation();
  const [isProcessing, setIsProcessing] = useState(false);
  const toast = useToast();

  const handleDownload = async (): Promise<void> => {
    try {
      setIsProcessing(true);
      const data = await createPresignedDownloadUrl({
        variables: { input: { fileId } },
      });
      const url = data.data?.createPresignedDownloadUrl.url;
      if (!url) {
        throw new Error(`Received nothing from server`);
      }
      // temporary fix for when window.open gets blocked
      // ideally, we can open a new window on click that will redirect
      // to the appropriate URL but that requires more effort
      const openedWindow = window.open(url, '_blank');
      if (!openedWindow) {
        window.location.href = url;
      } else {
        openedWindow.focus();
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setIsProcessing(false);
    }
  };

  if (asLink) {
    return (
      <LinkButton
        className={className}
        onClick={handleDownload}
        disabled={isProcessing}
      >
        {children}
      </LinkButton>
    );
  }

  return (
    <Button
      className={className}
      onClick={handleDownload}
      disabled={isProcessing}
      color={color}
    >
      {children}
    </Button>
  );
}

export default PresignedDownloadButton;
